import type { InternalAxiosRequestConfig } from 'axios'
import qs from 'qs'

import { QS_CONFIG } from '../../constants/api'
import { isIOS, isPWA } from '../device'
import { getPublicAuth } from '../getPublicAuth'
import { isInNativeApp } from '../nativeBridge'
import { decryptUser } from '../user'

// Request types that must never carry the user's privateAuth / username /
// password — typically pre-auth lookups like duplicate-contact checks.
//
// `try_play_game_websocket2` is the play-for-fun game launch. A logged-in
// player tapping "demo" must issue the same request a guest does, so the
// backend mints a demo session rather than a real-money one; injecting their
// credentials here would defeat that. This is a no-op for actual guests —
// they have no privateAuth and no stored user, so nothing was injected anyway.
const PUBLIC_TYPES: ReadonlySet<string> = new Set([
  'verify_parameters',
  'try_play_game_websocket2',
])

// An auth-source outage (increase.php down / hung / returning garbage) must
// not fail the request outright: the guest bootstrap POSTs (get_notice,
// get_app_version, …) are served by the backend even with no auth at all
// (verified against prod), and endpoints that DO validate reject server-side
// exactly as they would have with a bad hash. Flows that need a guaranteed
// auth (login, token refresh) set it explicitly in their body and never take
// this path.
const getPublicAuthOrNull = () => getPublicAuth().catch(() => null)

export async function addCredentialsToRequest(
  request: InternalAxiosRequestConfig,
) {
  const user = decryptUser()
  const lang = localStorage.getItem('i18nextLng') ?? 'es'
  const privateAuth = localStorage.getItem('privateAuth')

  if (request.data instanceof FormData) {
    const auth = request.data.get('auth')
    const username = request.data.get('username')
    const password = request.data.get('password')

    if (!auth) {
      if (privateAuth) {
        request.data.append('auth', privateAuth)
      } else {
        const publicAuth = await getPublicAuthOrNull()
        if (publicAuth) {
          request.data.append('auth', publicAuth)
        }
      }
    }

    // ISSUE: When QS_CONFIG has encode: false, special characters like +, &, =, % in user credentials
    // get misinterpreted during qs.stringify() and qs.parse() operations. For example:
    // - Username "user+name" becomes "user name" (space instead of +)
    // - Password "pass=word" breaks key-value parsing
    // - Characters like & and % can cause query string parsing errors
    //
    // FIX: By stringifying and then parsing the user object with QS_CONFIG, we ensure
    // that any special characters are properly handled according to the encoding rules,
    // preventing data corruption when credentials are added to FormData.
    const userStringified = qs.stringify(user, QS_CONFIG)
    const parsedUser = qs.parse(userStringified, QS_CONFIG) as Record<
      string,
      string
    >

    if (!username && parsedUser?.username) {
      request.data.append('username', parsedUser?.username)
    }

    if (!password && parsedUser?.password) {
      request.data.append('password', parsedUser?.password)
    }

    if (lang === 'zh') {
      request.data.append('language', '1')
    }

    // Add display_platform in payload
    const isNativeApp = isInNativeApp()
    request.data.append(
      'display_platform',
      isNativeApp ? '1' : isPWA() ? '2' : '0',
    )
    if (isNativeApp) {
      request.data.append('device_platform', isIOS() ? 'ios' : 'android')
    }
  } else if (typeof request.data === 'string') {
    let requestData = qs.parse(request.data)
    const { auth, username, password } = requestData

    const isPublicType =
      typeof requestData.type === 'string' && PUBLIC_TYPES.has(requestData.type)

    if (!auth) {
      if (privateAuth && !isPublicType) {
        requestData = { ...requestData, auth: privateAuth }
      } else {
        const publicAuth = await getPublicAuthOrNull()
        if (publicAuth) {
          requestData = { ...requestData, auth: publicAuth }
        }
      }
    }

    if (!isPublicType) {
      if (!username && user?.username)
        requestData = { ...requestData, username: user?.username }

      if (!password && user?.password)
        requestData = { ...requestData, password: user?.password }
    }

    if (lang === 'zh') {
      requestData = { ...requestData, language: '1' }
    }

    // Add display_platform in payload
    const isNativeApp = isInNativeApp()
    requestData = {
      ...requestData,
      display_platform: isNativeApp ? '1' : isPWA() ? '2' : '0',
      ...(isNativeApp && {
        device_platform: isIOS() ? 'ios' : 'android',
      }),
    }

    const updatedRequestDataQueryString = qs.stringify(requestData, QS_CONFIG)

    request.data = updatedRequestDataQueryString
  }

  return request
}
